import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  Signal,
  signal,
} from '@angular/core';

import {
  CurrencyComponent,
  Icon,
  IconComponent,
  PrimengDialogCustomCloseEventDirective,
  PrimengDialogDirective,
  SlimButtonComponent,
  SlimButtonTheme,
} from '@web/shared/ui';
import { PublicSales } from '../../models/public-sales';
import { PublicCartService } from '../../services/public-cart.service';
import { PublicCartAddItem } from '../../models/public-cart-add-item';
import { HttpErrorService, NavigatorService, Route } from '@web/common';
import { Dialog } from 'primeng/dialog';
import { isNil, isNotNil, Nillable, valueOrDefault } from '@web/utils';
import { PublicCartAddButtonDialogResult } from '../../types/public-cart-add-button-dialog-result';

@Component({
  selector: 'app-public-cart-add-button',
  imports: [
    IconComponent,
    SlimButtonComponent,
    Dialog,
    PrimengDialogDirective,
    PrimengDialogCustomCloseEventDirective,
    CurrencyComponent,
  ],
  templateUrl: './public-cart-add-button.component.html',
  styleUrl: './public-cart-add-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicCartAddButtonComponent {
  private readonly cart = inject(PublicCartService);
  private readonly httpError = inject(HttpErrorService);
  private readonly navigator = inject(NavigatorService);

  public readonly sales = input.required<PublicSales>();
  public readonly theme = input<SlimButtonTheme>('primary');
  public readonly label = input.required<string>();
  public readonly unavailableLabel = input<string>('Wyprzedane');
  public readonly inProgressLabel = input<string>('Przetwarzanie...');
  public readonly maxWidth = input<string>('auto');
  public readonly quantity = input<number>(1);

  /* prettier-ignore */
  protected readonly dialogResult = signal<Nillable<PublicCartAddButtonDialogResult>>(null);
  protected readonly isButtonAvailable = signal<boolean>(true);
  protected readonly isDialogVisible = signal<boolean>(false);
  protected readonly isAvailable = this.isAvailableSignal();
  protected readonly Icon = Icon;
  protected readonly isNotNil = isNotNil;
  protected readonly isNil = isNil;
  protected readonly valueOrDefault = valueOrDefault;

  public handleGoToCartClick(): void {
    this.navigator.navigateByRouterLink((provider) =>
      provider.routerLink(Route.PL_ShoppingCartDetails),
    );

    this.markDialogAsInvisible();
  }

  protected handleClickEvent(): void {
    this.markButtonAsDisabled();

    this.cart
      .addItem(
        new PublicCartAddItem({
          quantity: this.quantity(),
          id: this.sales().id,
          subProductId: this.sales().subProductId,
        }),
      )
      .subscribe({
        next: (item) => {
          this.dialogResult.set({ item });
          this.markDialogAsVisible();
          this.markButtonAsActive();
        },
        error: (response) => {
          const errorMessage = this.httpError
            .extract(response)
            .multiLineMessages();

          this.dialogResult.set({ errorMessage });
          this.markDialogAsVisible();
          this.markButtonAsActive();
        },
      });
  }

  protected round(amount: number): number {
    return Math.ceil(amount * 100) / 100;
  }

  protected handleContinueClick(): void {
    this.markDialogAsInvisible();
  }

  protected readonly customCloseEvent = () => {
    this.markDialogAsInvisible();
  };

  private markDialogAsVisible() {
    this.isDialogVisible.set(true);
  }

  private markDialogAsInvisible() {
    this.isDialogVisible.set(false);
  }

  private markButtonAsActive() {
    this.isButtonAvailable.set(true);
  }

  private markButtonAsDisabled() {
    this.isButtonAvailable.set(false);
  }

  private isAvailableSignal(): Signal<boolean> {
    return computed(() => this.sales().isAvailable());
  }
}
